import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  MessageCircle,
  Printer,
  Truck,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { printBillReceipt } from "@/lib/billPrint";
import { money } from "@/lib/format";

export const STATUS_TONES = {
  new: "blue",
  confirmed: "purple",
  preparing: "amber",
  out_for_delivery: "blue",
  delivered: "green",
  picked_up: "green",
  cancelled: "red",
};

const STATUS_ACTIONS = {
  new: [
    { status: "confirmed", label: "Confirm" },
    { status: "cancelled", label: "Cancel" },
  ],
  confirmed: [
    { status: "preparing", label: "Start preparing" },
    { status: "cancelled", label: "Cancel" },
  ],
  preparing: [
    { status: "out_for_delivery", label: "Out for delivery", modes: ["delivery"] },
    { status: "picked_up", label: "Picked up", modes: ["pickup"] },
    { status: "cancelled", label: "Cancel" },
  ],
  out_for_delivery: [
    { status: "delivered", label: "Delivered" },
    { status: "cancelled", label: "Cancel" },
  ],
};

export function formatStatus(s) {
  return (s || "").replace(/_/g, " ");
}

export function isCallSource(source) {
  return String(source || "").toLowerCase() === "call";
}

export function sourceBadge(source, t) {
  return isCallSource(source) ? (
    <Pill tone="amber">{t("onlineOrders.sourceCallBadge")}</Pill>
  ) : (
    <Pill tone="blue">{t("onlineOrders.sourceOnlineBadge")}</Pill>
  );
}

export function OnlineOrderDetailSheet({
  orderId,
  branchId,
  currency,
  onClose,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [driverId, setDriverId] = useState("");

  const { data: order, isLoading: detailLoading } = useQuery({
    queryKey: ["online-order", orderId],
    queryFn: async () => {
      const { data: res } = await api.get(`/online-orders/${orderId}`, {
        params: { branch_id: branchId },
      });
      return res.order;
    },
    enabled: Boolean(orderId && branchId),
  });

  const { data: driversData } = useQuery({
    queryKey: ["delivery-persons", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery-persons", {
        params: { branch_id: branchId },
      });
      return res;
    },
    enabled: Boolean(branchId && !readOnly),
    retry: false,
  });

  const drivers = driversData?.delivery_persons ?? driversData?.data ?? [];

  React.useEffect(() => {
    if (order) {
      setDriverId(order.delivery_person_id ? String(order.delivery_person_id) : "");
    }
  }, [order?.id, order?.delivery_person_id]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["online-orders"] });
    queryClient.invalidateQueries({ queryKey: ["online-order", orderId] });
    queryClient.invalidateQueries({ queryKey: ["online-orders-inbox"] });
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) =>
      api.post(`/online-orders/${id}/status`, { status }, { params: { branch_id: branchId } }),
    onSuccess: () => {
      toast.success("Status updated");
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not update status"),
  });

  const assignMutation = useMutation({
    mutationFn: ({ id, delivery_person_id }) =>
      api.post(
        `/online-orders/${id}/assign-driver`,
        { delivery_person_id: delivery_person_id || null },
        { params: { branch_id: branchId } },
      ),
    onSuccess: () => {
      toast.success("Driver assigned");
      invalidate();
    },
    onError: (err) => toast.error(err.response?.data?.message || "Assignment failed"),
  });

  const invoiceMutation = useMutation({
    mutationFn: (id) =>
      api.get(`/online-orders/${id}/invoice`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      const job = res.data.print_receipt;
      if (!job?.html || !printBillReceipt(job, job.bill_number || "Invoice")) {
        toast.error("Could not open print dialog — allow pop-ups if blocked");
        return;
      }
      toast.success(job.printer ? `Printing on ${job.printer.name}` : "Print dialog opened");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not load invoice"),
  });

  const whatsappMutation = useMutation({
    mutationFn: (id) =>
      api.get(`/online-orders/${id}/whatsapp-status`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      if (res.data.whatsapp_url) {
        window.open(res.data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
    },
    onError: (err) => toast.error(err.response?.data?.message || "Could not open WhatsApp"),
  });

  const actionsFor = (o) => {
    const base = STATUS_ACTIONS[o?.status] || [];
    return base.filter((a) => !a.modes || a.modes.includes(o?.mode));
  };

  return (
    <Sheet open={Boolean(orderId)} onOpenChange={(open) => !open && onClose?.()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{order?.reference || "Order"}</SheetTitle>
        </SheetHeader>

        {detailLoading || !order ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Pill tone={STATUS_TONES[order.status] || "default"}>{formatStatus(order.status)}</Pill>
              <Pill tone="default" className="capitalize">
                {order.mode}
              </Pill>
              {sourceBadge(order.source, t)}
            </div>

            <div className="text-sm space-y-1">
              <p className="font-medium">{order.customer_name}</p>
              <p className="text-slate-600">{order.customer_phone}</p>
              {order.address && <p className="text-slate-600">{order.address}</p>}
              {order.notes && <p className="text-slate-500 italic">{order.notes}</p>}
            </div>

            <Card className="p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase">Items</p>
              {(order.lines || []).map((line) => (
                <div key={line.id} className="flex justify-between text-sm gap-2">
                  <span>
                    {line.qty}× {line.product_name}
                    {line.variant_labels?.length > 0 && (
                      <span className="block text-[11px] text-slate-400">
                        {line.variant_labels.join(" · ")}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0">
                    {money(line.line_total, order.currency_code || currency)}
                  </span>
                </div>
              ))}
              <div className="border-t pt-2 text-sm font-semibold flex justify-between">
                <span>Total</span>
                <span>{money(order.net_amount, order.currency_code || currency)}</span>
              </div>
            </Card>

            {(order.status_logs || []).length > 0 && (
              <Card className="p-3 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">History</p>
                {(order.status_logs || []).map((log, idx) => (
                  <div key={`${log.status}-${idx}`} className="text-xs text-slate-600 flex justify-between gap-2">
                    <span>
                      <span className="font-medium capitalize">{formatStatus(log.status)}</span>
                      {log.user ? ` · ${log.user}` : ""}
                      {log.note ? ` — ${log.note}` : ""}
                    </span>
                    <span className="shrink-0 text-slate-400">
                      {log.at ? new Date(log.at).toLocaleString() : ""}
                    </span>
                  </div>
                ))}
              </Card>
            )}

            {!readOnly && order.mode === "delivery" && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5" /> Driver
                </p>
                <div className="flex gap-2">
                  <Select
                    value={driverId || "__none__"}
                    onValueChange={(v) => setDriverId(v === "__none__" ? "" : v)}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Assign driver" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {drivers.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.name}
                          {d.phone ? ` · ${d.phone}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={assignMutation.isPending}
                    onClick={() =>
                      assignMutation.mutate({
                        id: order.id,
                        delivery_person_id: driverId ? Number(driverId) : null,
                      })
                    }
                  >
                    Save
                  </Button>
                </div>
                {order.delivery_person_name && (
                  <p className="text-xs text-slate-500">Current: {order.delivery_person_name}</p>
                )}
              </div>
            )}

            {!readOnly && (
              <div className="flex flex-wrap gap-2">
                {actionsFor(order).map((a) => (
                  <Button
                    key={a.status}
                    size="sm"
                    variant={a.status === "cancelled" ? "outline" : "default"}
                    className={a.status === "cancelled" ? "text-red-600" : ""}
                    disabled={statusMutation.isPending}
                    onClick={() => statusMutation.mutate({ id: order.id, status: a.status })}
                  >
                    {a.label}
                  </Button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t pt-3">
              <Button
                size="sm"
                variant="outline"
                disabled={invoiceMutation.isPending}
                onClick={() => invoiceMutation.mutate(order.id)}
              >
                <Printer className="h-4 w-4" /> Print invoice
              </Button>
              {order.whatsapp_url && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(order.whatsapp_url, "_blank", "noopener,noreferrer")}
                >
                  <ExternalLink className="h-4 w-4" /> Open WhatsApp link
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={whatsappMutation.isPending}
                onClick={() => whatsappMutation.mutate(order.id)}
              >
                <MessageCircle className="h-4 w-4" /> Status via WhatsApp
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
